import { useEffect, useState } from "react";
import api from "../../api/axios";
import { useMainCompany } from '../../context/MainCompanyContext';
import { Pencil, Trash, PlusCircle, Scissors } from "lucide-react";
import Modal from "../../components/common/AdminModal";
import SubCompanyForm from "../../components/admin/SubCompanyForm";
import AdminPageHeader from "../../components/common/AdminPageHeader";
import LogoResizerModal from "../../components/admin/LogoResizerModal";
import Toast from "../../components/common/Toast";
import { Editor } from "@tinymce/tinymce-react";
import config from "../../config/config";

export default function AdminMainCompany() {
  const [masterThemes, setMasterThemes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success', visible: false });
  const [modal, setModal] = useState({ type: null, data: null });
  const [filters] = useState({ company: "", createdFrom: "", createdTo: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { loadMainCompanies } = useMainCompany();

  const showToast = (message, type = 'success', duration = 5000) => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), duration);
  };

  // Load master themes
  useEffect(() => {
    api.get("/admin/master-themes")
      .then(res => setMasterThemes(res.data?.data || []))
      .catch(() => setMasterThemes([]));
  }, []);

  // Fetch companies
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/main-companies", {
        params: { ...filters, page, pageSize },
      });
      setCompanies(data?.data || []);
    } catch (err) {
      console.error("Error fetching companies:", err);
      showToast("Could not load companies. Please try again.", 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    // Intentionally omit fetchCompanies to avoid refetch on every callback identity change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, pageSize]);

  const filteredCompanies = companies.filter((c) =>
    (c.main_company_name || "")
      .toLowerCase()
      .includes(filters.company.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCompanies.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const currentCompanies = filteredCompanies.slice(startIndex, startIndex + pageSize);

  // Delete confirmation logic
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState(null);

  const openDelete = (company) => {
    setDeleteTarget(company);
    setDeletePassword('');
    setDeleteError(null);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/main-companies/${deleteTarget.id}`, {
        data: { password: deletePassword },
        skipAuthRedirect: true,
      });
      showToast("Company deleted successfully.", 'success');
      setCompanies((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      if (typeof loadMainCompanies === 'function') loadMainCompanies();
      setShowDeleteConfirm(false);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Delete failed';
      if (err?.response?.status === 403) setDeleteError('Invalid delete password');
      else {
        setDeleteError(msg);
        showToast(msg, 'error');
      }
    }
  };

  // Form Component
  const CompanyForm = ({ type, data, onClose, onSaved }) => {
    const derivePageTitleSameAsCompany = (d) => {
      if (!d) return true;
      if (d.page_title_same_as_company === true || d.page_title_same_as_company === 1)
        return true;
      if (d.page_title_same_as_company === false || d.page_title_same_as_company === 0)
        return false;
      const pt = String(d.page_title ?? "").trim();
      const cn = String(d.main_company_name ?? "").trim();
      return !pt || pt === cn;
    };

    const initialPageTitleSame = derivePageTitleSameAsCompany(data);
    const [formData, setFormData] = useState({
      companyName: data?.main_company_name || "",
      domainName: data?.domain_name || "",
      page_title: initialPageTitleSame ? "" : String(data?.page_title ?? "").trim(),
      page_title_same_as_company: initialPageTitleSame,
      pwa_short_name: data?.pwa_short_name || "",
      pwa_description: data?.pwa_description || "",
      pwa_theme_color: data?.pwa_theme_color || "",
      logo: null,
      logo_dash: null,
      favicon: null,
      background_image: null,
      css_content: data?.css_content || "",
      register_confirmation: data?.register_confirmation || false,
      commission_buyer: !!(data?.commission_buyer === true || data?.commission_buyer === 1),
      theme_id: data?.theme_id ? String(data.theme_id) : "",
    });

    const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
    const [tempThemeId, setTempThemeId] = useState(null);
    const [prevThemeId, setPrevThemeId] = useState(null);
    const [adminPassword, setAdminPassword] = useState('');
    const [showDevPasswordPrompt, setShowDevPasswordPrompt] = useState(false);
    const [developerPassword, setDeveloperPassword] = useState('');
    const [pendingBody, setPendingBody] = useState(null);
    const [errors, setErrors] = useState({});
    const [devVerifyError, setDevVerifyError] = useState(null);
    const errorKeyMap = {
      companyName: ['main_company_name', 'companyName', 'name'],
      domainName: ['domain_name', 'domainName'],
      pwa_short_name: ['pwa_short_name'],
      pwa_description: ['pwa_description'],
      pwa_theme_color: ['pwa_theme_color'],
      page_title: ['page_title'],
      commission_buyer: ['commission_buyer'],
      theme_id: ['theme_id'],
      css_content: ['css_content'],
      logo: ['logo'],
      logo_dash: ['logo_dash'],
      favicon: ['favicon'],
      background_image: ['background_image'],
    };

    // Compact server error parser: returns an errors map keyed by canonical field and form keys.
    const parseServerErrors = (resp) => {
      const canonicalize = (k) => {
        if (!k) return k;
        try {
          return String(k).replace(/\[\d+\]/g, '').split('.').pop();
        } catch (e) {
          return k;
        }
      };

      const normalized = {};

      if (resp?.errors) {
        if (Array.isArray(resp.errors)) {
          // array of { field, message }
          resp.errors.forEach((it) => {
            if (!it) return;
            if (it.field) normalized[canonicalize(it.field)] = it.message || it.msg || String(it);
            else if (typeof it === 'string') normalized._global = (normalized._global ? normalized._global + ' ' : '') + it;
          });
        } else if (typeof resp.errors === 'object') {
          Object.entries(resp.errors).forEach(([k, v]) => {
            const ck = canonicalize(k);
            normalized[ck] = Array.isArray(v) ? v.join(' ') : String(v);
          });
        }
      } else if (resp && typeof resp === 'object') {
        // flat map like { field: ['msg'] }
        Object.entries(resp).forEach(([k, v]) => {
          if (k === 'message') return;
          const ck = canonicalize(k);
          if (Array.isArray(v)) normalized[ck] = v.join(' ');
          else if (typeof v === 'string') normalized[ck] = v;
        });
      }

      // Map backend keys to form keys
      const mapped = { ...normalized };
      Object.keys(errorKeyMap).forEach((formKey) => {
        const backKeys = errorKeyMap[formKey] || [];
        for (const bk of backKeys) {
          const cbk = canonicalize(bk);
          if (normalized[cbk] && !mapped[formKey]) mapped[formKey] = normalized[cbk];
          if (normalized[cbk]) mapped[cbk] = normalized[cbk];
        }
      });

      return mapped;
    };

    const getFieldError = (field) => {
      const keys = errorKeyMap[field] || [field];
      for (const k of keys) if (errors && errors[k]) return errors[k];
      return null;
    };

    const clearFieldErrors = (field) => {
      const keys = errorKeyMap[field] || [field];
      setErrors((prev) => {
        const copy = { ...(prev || {}) };
        keys.forEach((k) => { if (k in copy) delete copy[k]; });
        return copy;
      });
    };

    const [previewLogoUrl, setPreviewLogoUrl] = useState(
      data?.logo ? `${config.assetsBaseUrl}${data.logo}` : null
    );
    const [previewLogoDashUrl, setPreviewLogoDashUrl] = useState(
      data?.logo_dash ? `${config.assetsBaseUrl}${data.logo_dash}` : null
    );
    const [previewFaviconUrl, setPreviewFaviconUrl] = useState(
      data?.favicon ? `${config.assetsBaseUrl}${data.favicon}` : null
    );
    const [previewBgUrl, setPreviewBgUrl] = useState(
      data?.background_image ? `${config.assetsBaseUrl}${data.background_image}` : null
    );

    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [editCssChecked, setEditCssChecked] = useState(false);

    const fieldInputClass =
      "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-600";
    const fieldLabelClass = "block text-sm font-medium text-slate-700 mb-1";

    useEffect(() => {
      if (type === "edit" && data?.css_file_path) {
        const cssPath = data.css_file_path;
        const href = cssPath.startsWith("http") || cssPath.startsWith("blob:")
          ? cssPath
          : `${config.assetsBaseUrl.replace(/\/$/, "")}/uploads/${String(cssPath).replace(/^\/+/, "")}`;
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        document.head.appendChild(link);
        return () => document.head.removeChild(link);
      }
    }, [data, type]);

    const DASH_LOGO_MIN_W = 200;
    const DASH_LOGO_MAX_W = 650;
    const DASH_LOGO_MIN_H = 60;
    const DASH_LOGO_MAX_H = 440;

    const handleChange = (e) => {
      const { name, value, files, type } = e.target;
      if (files && files[0]) {
        const file = files[0];
        const previewUrl = URL.createObjectURL(file);

        if (name === "logo_dash") {
          const img = new Image();
          img.onload = () => {
            const w = img.naturalWidth;
            const h = img.naturalHeight;
            if (w < DASH_LOGO_MIN_W || w > DASH_LOGO_MAX_W || h < DASH_LOGO_MIN_H || h > DASH_LOGO_MAX_H) {
              URL.revokeObjectURL(previewUrl);
              setErrors((prev) => ({
                ...prev,
                logo_dash: `Dashboard logo must be about ${DASH_LOGO_MIN_W}–${DASH_LOGO_MAX_W}px wide and ${DASH_LOGO_MIN_H}–${DASH_LOGO_MAX_H}px height (this image is ${w}×${h}px).`,
              }));
              setFormData((prev) => ({ ...prev, logo_dash: null }));
              setPreviewLogoDashUrl(
                data?.logo_dash ? `${config.assetsBaseUrl}${data.logo_dash}` : null
              );
              e.target.value = "";
              return;
            }
            clearFieldErrors("logo_dash");
            setPreviewLogoDashUrl(previewUrl);
            setFormData((prev) => ({ ...prev, logo_dash: file }));
          };
          img.onerror = () => {
            URL.revokeObjectURL(previewUrl);
            setErrors((prev) => ({
              ...prev,
              logo_dash: "Could not read that image. Try another file.",
            }));
            setFormData((prev) => ({ ...prev, logo_dash: null }));
            setPreviewLogoDashUrl(
              data?.logo_dash ? `${config.assetsBaseUrl}${data.logo_dash}` : null
            );
            e.target.value = "";
          };
          img.src = previewUrl;
          return;
        }

        if (name === "logo") setPreviewLogoUrl(previewUrl);
        if (name === "favicon") setPreviewFaviconUrl(previewUrl);
        if (name === "background_image") setPreviewBgUrl(previewUrl);
        clearFieldErrors(name);
        setFormData((prev) => ({ ...prev, [name]: file }));
        return;
      }
      if (type === "checkbox") {
        if (name === "page_title_same_as_company") {
          clearFieldErrors("page_title");
          const nextChecked = e.target.checked;
          setFormData((prev) => ({
            ...prev,
            page_title_same_as_company: nextChecked,
            page_title: nextChecked
              ? ""
              : (prev.page_title || "").trim() || (prev.companyName || "").trim(),
          }));
          return;
        }
        clearFieldErrors(name);
        setFormData((prev) => ({ ...prev, [name]: e.target.checked }));
        return;
      }
      clearFieldErrors(name);
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleThemeChange = (e) => {
      const newThemeId = e.target.value;
      // allow clearing the theme (empty value) immediately
      if (newThemeId === "") {
        setFormData((prev) => ({ ...prev, theme_id: "" }));
        return;
      }

      // if an existing theme is being changed to a different non-empty theme, prompt for confirmation
      if (data?.theme_id && data.theme_id !== parseInt(newThemeId)) {
        // simply update selection (no immediate password prompt)
        setFormData((prev) => ({ ...prev, theme_id: String(newThemeId) }));
        return;
      }

      setFormData((prev) => ({ ...prev, theme_id: String(newThemeId) }));
    };

    const validateForm = () => {
      const e = {};
      const name = (formData.companyName || '').trim();
      const domain = (formData.domainName || '').trim();

      if (!name) e.companyName = 'Company name is required.';

      if (!domain) e.domainName = 'Domain name is required.';
      else {
        // Basic domain validation (hostPattern/urlPattern) can be re-enabled here if needed
      }

      if (!formData.page_title_same_as_company) {
        const pt = (formData.page_title || "").trim();
        if (!pt) {
          e.page_title =
            "Enter a browser tab title, or turn on “Same as company name”.";
        }
      }

      setErrors(e);
      return Object.keys(e).length ? e : null;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      // client-side validation
      const clientErr = validateForm();
      if (clientErr) {
        // focus is left to the browser; prevent submit
        return;
      }
      const body = new FormData();
      body.append("main_company_name", formData.companyName);
      body.append("domain_name", formData.domainName);
      const resolvedPageTitle = formData.page_title_same_as_company
        ? (formData.companyName || "").trim()
        : (formData.page_title || "").trim();
      body.append("page_title", resolvedPageTitle);
      body.append(
        "page_title_same_as_company",
        formData.page_title_same_as_company ? "1" : "0"
      );
      if (type === "edit" && editCssChecked) {
        body.append("pwa_short_name", formData.pwa_short_name ?? "");
        body.append("pwa_description", formData.pwa_description ?? "");
        body.append("pwa_theme_color", formData.pwa_theme_color ?? "");
      }
      body.append("register_confirmation", formData.register_confirmation);
      body.append("commission_buyer", formData.commission_buyer ? "1" : "0");
      if (type === 'edit' && editCssChecked) body.append("css_content", formData.css_content);
      if (type === 'edit' && formData.theme_id) body.append("theme_id", formData.theme_id);

      if (type === 'edit' && editCssChecked && formData.translation_content) {
        body.append("translation_content", formData.translation_content);
      }


      if (formData.logo) body.append("logo", formData.logo);
      if (formData.logo_dash) body.append("logo_dash", formData.logo_dash);
      if (formData.favicon) body.append("favicon", formData.favicon);
      if (formData.background_image) body.append("background_image", formData.background_image);

      setUploading(true);
      // Submit helper (keeps single place for actual network call)
      const submitBody = async (formBody) => {
        const method = type === "edit" ? "put" : "post";
        const url = type === "edit" ? `/admin/main-companies/${data.id}` : "/admin/main-companies";
        try {
          await api[method](url, formBody, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (evt) => {
              if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
            },
          });
          setUploading(false);
          setPendingBody(null);
          setDeveloperPassword('');
          setShowDevPasswordPrompt(false);
          if (typeof onSaved === 'function') await onSaved();
          onClose();
        } catch (err) {
          setUploading(false);
          const resp = err?.response?.data || {};
          const parsed = parseServerErrors(resp || {});
          if (Object.keys(parsed).length > 0) {
            setErrors(parsed);
            if (resp.message) showToast(resp.message, 'error');
          } else if (resp?.message) {
            setErrors({ _global: resp.message });
            showToast(resp.message, 'error');
          } else {
            showToast('Save failed. Please try again.', 'error');
          }
        }
      };

      // If editing and either theme changed or CSS editing is requested, require developer password verification
      const themeChanged = type === 'edit' && String(formData.theme_id || '') !== String(data?.theme_id || '');
      const requiresDevVerify = type === 'edit' && (themeChanged || editCssChecked);

      if (requiresDevVerify) {
        // store pending body and open dev password modal
        setPendingBody(body);
        setShowDevPasswordPrompt(true);
        setUploading(false);
        return;
      }

      // otherwise submit immediately
      await submitBody(body);
    };

    const pageTitleInputValue = formData.page_title_same_as_company
      ? (formData.companyName || "").trim()
      : formData.page_title;

    return (
      <form onSubmit={handleSubmit} className="space-y-5">
        {errors._global && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm">
            {errors._global}
          </div>
        )}

        <section className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-4">
          <div className="border-b border-slate-200 pb-2">
            <h3 className="text-sm font-semibold text-slate-800">Company &amp; domain</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Legal or display name, host, and how the browser tab is titled for this tenant.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={fieldLabelClass}>Company name *</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className={fieldInputClass}
              />
              {getFieldError("companyName") && (
                <div className="text-red-600 text-sm mt-1">{getFieldError("companyName")}</div>
              )}
            </div>
            <div>
              <label className={fieldLabelClass}>Domain name *</label>
              <input
                type="text"
                name="domainName"
                value={formData.domainName}
                onChange={handleChange}
                placeholder="example.com"
                className={fieldInputClass}
              />
              {getFieldError("domainName") && (
                <div className="text-red-600 text-sm mt-1">{getFieldError("domainName")}</div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={fieldLabelClass}>Browser tab title</label>
              <p className="text-xs text-slate-500 -mt-1 mb-2">
                Shown in the window / tab title 
                <code className="text-[11px] bg-slate-100 px-1 rounded">page_title</code>).
              </p>
              <input
                type="text"
                name="page_title"
                value={pageTitleInputValue}
                onChange={handleChange}
                disabled={formData.page_title_same_as_company}
                className={fieldInputClass}
                placeholder="e.g. Metro United Title and Escrow"
              />
              <label className="inline-flex items-center gap-2 mt-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="page_title_same_as_company"
                  checked={!!formData.page_title_same_as_company}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">Same as company name</span>
              </label>
              {getFieldError("page_title") && (
                <div className="text-red-600 text-sm mt-1">{getFieldError("page_title")}</div>
              )}
            </div>
            <div>
              <label className={fieldLabelClass}>Allow Commission on the Buyer’s</label>
              <p className="text-xs text-slate-500 -mt-1 mb-2">
                Make the Commission section on the Buyer’s module optional
              </p>
              <label className="inline-flex items-center gap-2 mt-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="commission_buyer"
                  checked={!!formData.commission_buyer}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">Checked - always show</span>
              </label>
              {getFieldError("commission_buyer") && (
                <div className="text-red-600 text-sm mt-1">{getFieldError("commission_buyer")}</div>
              )}
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-4 shadow-sm">
          <div className="border-b border-slate-100 pb-2">
            <h3 className="text-sm font-semibold text-slate-800">Branding &amp; theme</h3>
            <p className="text-xs text-slate-500 mt-0.5">Logos, favicon, background, and master theme (edit only).</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={fieldLabelClass}>
                Favicon <span className="text-xs font-normal text-slate-500">(192×192px)</span>
              </label>
              <input
                type="file"
                name="favicon"
                onChange={handleChange}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
              />
              {previewFaviconUrl && (
                <img
                  src={previewFaviconUrl}
                  alt="Favicon preview"
                  className="mt-2 h-10 w-10 border border-slate-200 rounded-lg object-cover"
                />
              )}
              {getFieldError("favicon") && (
                <div className="text-red-600 text-sm mt-1">{getFieldError("favicon")}</div>
              )}
            </div>
            <div>
              {type === "edit" ? (
                <>
                  <label className={fieldLabelClass}>Master theme</label>
                  <select
                    name="theme_id"
                    value={formData.theme_id || ""}
                    onChange={handleThemeChange}
                    className={fieldInputClass}
                  >
                    <option value="">— Select theme —</option>
                    {masterThemes.map((theme) => (
                      <option key={theme.id} value={theme.id}>
                        {theme.name}
                      </option>
                    ))}
                  </select>
                  {getFieldError("theme_id") && (
                    <div className="text-red-600 text-sm mt-1">{getFieldError("theme_id")}</div>
                  )}
                </>
              ) : (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 p-4 text-xs text-slate-500">
                  Theme can be assigned after this company is created (edit company).
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={fieldLabelClass}>
                Company logo{" "}
                <span className="text-xs font-normal text-slate-500">
                  (200–350px wide, 60–140px tall)
                </span>
              </label>
              <input
                type="file"
                name="logo"
                accept="image/*"
                onChange={handleChange}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
              />
              {previewLogoUrl && (
                <img
                  src={previewLogoUrl}
                  alt="Logo preview"
                  className="mt-2 h-16 border border-slate-200 rounded-lg object-contain bg-white"
                />
              )}
              {getFieldError("logo") && (
                <div className="text-red-600 text-sm mt-1">{getFieldError("logo")}</div>
              )}
            </div>
            <div>
              <label className={fieldLabelClass}>Background image</label>
              <input
                type="file"
                name="background_image"
                onChange={handleChange}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
              />
              {previewBgUrl && (
                <img
                  src={previewBgUrl}
                  alt="Background preview"
                  className="mt-2 h-24 max-w-full border border-slate-200 rounded-lg object-cover"
                />
              )}
              {getFieldError("background_image") && (
                <div className="text-red-600 text-sm mt-1">{getFieldError("background_image")}</div>
              )}
            </div>
          </div>

          <div>
            <label className={fieldLabelClass}>
              Dashboard logo{" "}
              <span className="text-xs font-normal text-slate-500">
                (optional, ~{DASH_LOGO_MIN_W}–{DASH_LOGO_MAX_W}px × {DASH_LOGO_MIN_H}–{DASH_LOGO_MAX_H}px)
              </span>
            </label>
            <input
              type="file"
              name="logo_dash"
              accept="image/*"
              onChange={handleChange}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
            />
            {previewLogoDashUrl && (
              <img
                src={previewLogoDashUrl}
                alt="Dashboard logo preview"
                className="mt-2 max-h-28 max-w-md border border-slate-200 rounded-lg object-contain bg-white"
              />
            )}
            {getFieldError("logo_dash") && (
              <div className="text-red-600 text-sm mt-1">{getFieldError("logo_dash")}</div>
            )}
          </div>
        </section>

        {/* CSS Editor (edit-only) */}
        {type === "edit" && (
          <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={editCssChecked}
                onChange={(e) => setEditCssChecked(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-slate-800">Edit PWA / CSS / translation</span>
            </label>

            {editCssChecked && (
              <div className="space-y-4 pt-1">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
                  <h3 className="text-sm font-semibold text-slate-800">PWA / Add to Home Screen</h3>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Each main company domain should behave as its own installable app. The favicon above is used as the home screen icon.
                    The live app name and manifest are resolved on the server for the visitor&apos;s host so the shortcut opens this company&apos;s domain.
                    Ensure your API serves a dynamic Web App Manifest (see project <code className="text-xs bg-white px-1 rounded">backend-examples/auth-web-manifest.route.example.js</code>) and optional same-origin path{' '}
                    <code className="text-xs bg-white px-1 rounded">/web-app-manifest.json</code>.
                    Set <code className="text-xs bg-white px-1 rounded">REACT_APP_ENABLE_PWA_SERVICE_WORKER=true</code> for Chromium install criteria when you are ready.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={fieldLabelClass}>PWA short name</label>
                      <input
                        type="text"
                        name="pwa_short_name"
                        value={formData.pwa_short_name}
                        onChange={handleChange}
                        maxLength={12}
                        placeholder="Shown under icon (≤12 chars)"
                        className={fieldInputClass}
                      />
                      {getFieldError('pwa_short_name') && <div className="text-red-600 text-sm mt-1">{getFieldError('pwa_short_name')}</div>}
                    </div>
                    <div>
                      <label className={fieldLabelClass}>Manifest theme color</label>
                      <input
                        type="text"
                        name="pwa_theme_color"
                        value={formData.pwa_theme_color}
                        onChange={handleChange}
                        placeholder="#1e40af"
                        className={`${fieldInputClass} font-mono`}
                      />
                      {getFieldError('pwa_theme_color') && <div className="text-red-600 text-sm mt-1">{getFieldError('pwa_theme_color')}</div>}
                    </div>
                  </div>
                  <div>
                    <label className={fieldLabelClass}>PWA description (optional)</label>
                    <input
                      type="text"
                      name="pwa_description"
                      value={formData.pwa_description}
                      onChange={handleChange}
                      maxLength={300}
                      placeholder="Short description for install UI / store-style hints"
                      className={fieldInputClass}
                    />
                    {getFieldError('pwa_description') && <div className="text-red-600 text-sm mt-1">{getFieldError('pwa_description')}</div>}
                  </div>
                </div>

                {/* CSS Editor */}
                <div>
                  <label className="block text-sm font-medium mb-1">Custom CSS (Company Theme)</label>
                  <Editor
                    apiKey="mpwzqoajpwgkybw09orm71pipq474ewfqapm6hde2mws0a0t"
                    init={{
                      height: 300,
                      menubar: false,
                      plugins: "code",
                      toolbar: "undo redo | bold italic underline | code",
                    }}
                    value={formData.css_content}
                    onEditorChange={(newValue) =>
                      setFormData((prev) => ({ ...prev, css_content: newValue }))
                    }
                  />
                </div>

                {/* Translation File Editor */}
                {(data?.translation_content || formData.translation_content) && (
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Translation File ({data.translation_file || "en.json"})
                    </label>
                    <Editor
                      apiKey="mpwzqoajpwgkybw09orm71pipq474ewfqapm6hde2mws0a0t"
                      init={{
                        height: 400,
                        menubar: false,
                        plugins: "code",
                        toolbar: "undo redo | code",
                        content_style: "body { font-family: monospace; }",
                      }}
                      value={formData.translation_content || data.translation_content || ""}
                      onEditorChange={(newValue) =>
                        setFormData((prev) => ({
                          ...prev,
                          translation_content: newValue,
                        }))
                      }
                    />
                  </div>
                )}

              </div>
            )}
          </section>
        )}


        {/* Buttons */}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={uploading}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {uploading ? `Saving... ${uploadProgress}%` : "Save"}
          </button>
        </div>

        {/* Nested password modal */}
        {showPasswordPrompt && (
          <Modal
            title="Confirm Theme Change"
            onClose={() => {
              // revert optimistic change
              setFormData((prev) => ({ ...prev, theme_id: prevThemeId }));
              setShowPasswordPrompt(false);
              setTempThemeId(null);
              setAdminPassword('');
              setPrevThemeId(null);
            }}
          >
            <p className="mb-3 text-gray-700">
              Changing the theme will immediately affect your live application.
              Please confirm the admin password to proceed.
            </p>

            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-3"
              placeholder="Enter admin password"
            />

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  // revert optimistic change
                  setFormData((prev) => ({ ...prev, theme_id: prevThemeId }));
                  setShowPasswordPrompt(false);
                  setTempThemeId(null);
                  setAdminPassword('');
                  setPrevThemeId(null);
                }}
                className="px-3 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>

              <button
                type="button"
                className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={async () => {
                  if (adminPassword.trim().length < 4) {
                    alert("Please enter a valid password");
                    return;
                  }

                  try {
                    // ✅ Verify password with backend (using global password in .env)
                    await api.put(`/admin/main-companies/${data.id}/theme`, {
                      theme_id: tempThemeId,
                      admin_password: adminPassword,
                    });

                    // ✅ Apply the theme locally after backend confirmation
                    setFormData((prev) => ({ ...prev, theme_id: tempThemeId }));
                    setShowPasswordPrompt(false);
                    setTempThemeId(null);
                    setAdminPassword('');
                    setPrevThemeId(null);

                    showToast("Theme changed successfully", "success");
                  } catch (err) {
                    const msg = err?.response?.data?.message || "Theme change failed";
                    showToast(msg, "error");
                  }
                }}
              >
                Confirm
              </button>
            </div>
          </Modal>
        )}

        {/* Developer password modal for edit actions (theme change or edit CSS) */}
        {showDevPasswordPrompt && (
          <Modal
            title="Developer Password Required"
            onClose={() => {
              setShowDevPasswordPrompt(false);
              setPendingBody(null);
              setDeveloperPassword('');
              setDevVerifyError(null);
            }}
          >
            <p className="mb-3 text-gray-700">
              Changing the theme or editing company CSS requires developer authorization.
              Please enter the developer password to continue.
            </p>

            <input
              type="password"
              value={developerPassword}
              onChange={(e) => setDeveloperPassword(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-3"
              placeholder="Developer password"
            />

            {devVerifyError && <div className="text-red-600 text-sm mt-1">{devVerifyError}</div>}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDevPasswordPrompt(false);
                  setPendingBody(null);
                  setDeveloperPassword('');
                  setDevVerifyError(null);
                }}
                className="px-3 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>

              <button
                type="button"
                className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={async () => {
                  // clear previous verification error
                  setDevVerifyError(null);
                  if (!developerPassword || developerPassword.trim().length < 3) {
                    setDevVerifyError('Enter developer password');
                    return;
                  }
                  try {
                    // verify developer password
                    const resp = await api.post('/admin/verify-password', { type: 'developer', password: developerPassword });
                    if (resp?.data?.status === 'success') {
                      // success: clear error, notify and proceed
                      setDevVerifyError(null);
                      showToast('Password verified', 'success');
                      // proceed to submit pending body
                      if (pendingBody) {
                        // reuse submitBody defined in handleSubmit by calling the same network code inline here
                        setUploading(true);
                        try {
                          const method = type === "edit" ? "put" : "post";
                          const url = type === "edit" ? `/admin/main-companies/${data.id}` : "/admin/main-companies";
                          await api[method](url, pendingBody, {
                            headers: { "Content-Type": "multipart/form-data" },
                            onUploadProgress: (evt) => {
                              if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
                            },
                          });
                          setUploading(false);
                          setPendingBody(null);
                          setDeveloperPassword('');
                          setShowDevPasswordPrompt(false);
                          setDevVerifyError(null);
                          if (typeof onSaved === 'function') await onSaved();
                          onClose();
                        } catch (err) {
                          setUploading(false);
                          const respErr = err?.response?.data || {};
                          const parsed = parseServerErrors(respErr || {});
                          if (Object.keys(parsed).length > 0) {
                            setErrors(parsed);
                            if (respErr.message) showToast(respErr.message, 'error');
                          } else if (respErr?.message) {
                            setErrors({ _global: respErr.message });
                            showToast(respErr.message, 'error');
                          } else {
                            showToast('Save failed. Please try again.', 'error');
                          }
                        }
                      }
                    } else {
                      // verification failed: keep modal open and show error on modal
                      const msg = resp?.data?.message || 'Invalid developer password';
                      setDevVerifyError(msg);
                    }
                  } catch (err) {
                    const msg = err?.response?.data?.message || 'Verification failed';
                    setDevVerifyError(msg);
                  }
                }}
              >
                Verify & Submit
              </button>
            </div>
          </Modal>
        )}
        {/* (CSS verification removed) */}
      </form>
    );
  };

  return (
    <div className="p-4">
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />

      <AdminPageHeader
        title="Manage Main Companies"
        actions={(
          <div className="flex flex-wrap gap-2">
            <button
              className="flex items-center gap-1 px-3 py-2 text-sm rounded bg-gray-600 text-white hover:bg-gray-700"
              onClick={() => setModal({ type: "resizeLogo" })}
            >
              <Scissors className="w-4 h-4" /> Resize Logo
            </button>
            <button
              className="flex items-center gap-1 px-3 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => setModal({ type: "create" })}
            >
              <PlusCircle className="w-4 h-4" /> Create Main Company
            </button>
          </div>
        )}
      />

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-2 py-2 text-left">Company</th>
              <th className="px-2 py-2 text-left">View</th>
              <th className="px-2 py-2 text-center">Created Date</th>
              <th className="px-2 py-2 text-center">Modified Date</th>
              <th className="px-2 py-2 text-left">Action</th>
              <th className="px-2 py-2 text-left">Sub Company</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-4 text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : currentCompanies.length > 0 ? (
              currentCompanies.map((c) => (
                <tr key={c.id} className="border-t hover:bg-gray-50">
                  <td className="px-2 py-2">
                    <a
                      href={`/admin/company/${c.id}`}
                      className="text-indigo-700 hover:underline"
                    >
                      {c.main_company_name}
                    </a>
                  </td>
                  <td className="px-2 py-2">
                    <a
                      href={(function () { try { const d = String(c.domain_name || '').trim(); return /^(https?:)?\/\//i.test(d) ? d : `https://${d}` } catch (e) { return c.domain_name } })()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 hover:underline"
                    >
                      {(function () { try { const d = String(c.domain_name || '').trim(); return /^(https?:)?\/\//i.test(d) ? d : `https://${d}` } catch (e) { return c.domain_name } })()}
                    </a>
                  </td>
                  <td className="px-2 py-2 text-center">
                    {new Date(c.created).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </td>
                  <td className="px-2 py-2 text-center">
                    {new Date(c.modified).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex gap-2">
                      <button
                        className="px-3 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 flex items-center gap-1"
                        onClick={() => setModal({ type: "edit", data: c })}
                      >
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button
                        className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 flex items-center gap-1"
                        onClick={() => openDelete(c)}
                      >
                        <Trash className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <button
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                      onClick={() => setModal({ type: "subcompany", data: c })}
                    >
                      <PlusCircle className="w-4 h-4" /> Create Sub Company
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-3 text-gray-500">
                  No results found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-3 text-sm">
          <span>
            Showing {startIndex + 1}–{Math.min(startIndex + pageSize, filteredCompanies.length)} of {filteredCompanies.length}
          </span>
          <div className="flex gap-2 items-center">
            <label>Rows</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="border rounded px-2 py-1"
            >
              {[5, 10, 25, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
            >
              Prev
            </button>
            <span>{page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Instructions for Setting Up a New Main Company */}
      <div className="mt-8 p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          Main Company Setup Instructions
        </h3>

        <p className="text-sm text-gray-600 mb-4">
          Please follow the steps below in the specified order when setting up a new main company.
        </p>

        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li>
            Create the <strong>Main Company</strong>. The support team will map the domain name to the main company.
          </li>
          <li>Create the <strong>Sub-Company</strong> under the main company.</li>
          <li>
            Configure <strong>Company Settings</strong> and set the <strong>Title Fees</strong> and
            <strong> Escrow Fees</strong> for each sub-company.
          </li>
          <li>Configure <strong>National Settings</strong> for the main company.</li>
          <li>Configure <strong>State Settings</strong> for each sub-company.</li>
          <li>Configure <strong>County Settings</strong> for each sub-company.</li>
          <li>Configure <strong>Disclaimer</strong> for the main company.</li>
          <li>Add <strong>Sales Representatives</strong> for each sub-company.</li>
          <li>Add a <strong>Master Account</strong> for the main company.</li>
        </ol>
        <p className="mt-4 text-sm text-gray-600 italic">
          <strong>Important:</strong> Ensure all steps are completed before making the company live.
        </p>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <Modal title="Delete Main Company" onClose={() => setShowDeleteConfirm(false)}>
          <div>
            <p>Are you sure you want to delete <strong>{deleteTarget?.main_company_name}</strong>?</p>
            <div className="mt-3">
              <label className="block text-sm">Enter delete password</label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="w-full border px-2 py-1 rounded mt-1"
              />
              {deleteError && <div className="text-red-600 text-sm mt-2">{deleteError}</div>}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-3 py-2 bg-gray-300 rounded">
                Cancel
              </button>
              <button onClick={handleDeleteConfirmed} className="px-3 py-2 bg-red-600 text-white rounded">
                Delete
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Resize Logo Modal (standalone: upload → crop/resize → download) */}
      {modal.type === "resizeLogo" && (
        <Modal
          title="Resize Logo"
          size="4xl"
          onClose={() => setModal({ type: null, data: null })}
        >
          <LogoResizerModal
            onClose={() => setModal({ type: null, data: null })}
            showToast={showToast}
          />
        </Modal>
      )}

      {/* Create/Edit/Subcompany Modals */}
      {modal.type && modal.type !== "resizeLogo" && (
        <Modal
          title={
            modal.type === "create"
              ? "Create Main Company"
              : modal.type === "edit"
                ? "Edit Main Company"
                : "Create Sub Company"
          }
          size="full"
          onClose={() => setModal({ type: null, data: null })}
        >
          {modal.type === "subcompany" ? (
            <SubCompanyForm
              mode="create"
              mainCompanyId={modal.data?.id}
              onClose={() => setModal({ type: null, data: null })}
              onCreated={async () => {
                const { data: refreshed } = await api.get("/admin/main-companies");
                setCompanies(refreshed?.data || []);
                showToast("Sub company created successfully.", "success");
              }}
            />
          ) : (
            <CompanyForm
              type={modal.type}
              data={modal.data}
              onClose={() => setModal({ type: null, data: null })}
              onSaved={async () => {
                await fetchCompanies();
                showToast(modal.type === 'create' ? 'Company created successfully.' : 'Company updated successfully.', 'success');
              }}
            />
          )}
        </Modal>
      )}
    </div>
  );
}
